import { Check, Close } from '@mui/icons-material'
import { Chip, Stack, Tooltip, Box, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { type ColumnProps } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { type Offer, type PriceObject, type Request } from './types'
import { parseNumericPrice } from './functions'
import Button from '../../../components/Button'
import { useUserContext } from '../../../utils/context'
import {
  getOrgUserRepresentation,
  getPKfromSelf,
  isPrivileged,
  renderDateTime,
} from '../../../utils/functions'
import { type OrganizationUser, type SelectOption, type SmartTableColumnType, type User } from '../../../utils/types'
import { type FormFieldType } from '../../Form'
type Props = {
  name:
    | 'requestTableColumns'
    | 'offerTableColumns'
    | 'certificateOptions'
    | 'requestFormFields'
    | 'requestFormFieldsDisabled'
    | 'requestFormFieldsStaff'
    | 'offerFormFields'
    | 'offerFormFieldsDisabled'
    | 'offerFormFieldsStaff'
    | 'addressFormFields'
    | 'requestSharepointArticleFields'
    | 'purchasedPartFormFields'
    | 'dimensionsFormFields'
    | 'sharepointFormFields'
    | 'editSharepointFormFields'
} & (
  | RequestTableKwargs
  | OfferTableKwargs
  | CertificateOptionsKwargs
  | RequestFormFieldsKwargs
  | RequestFormFieldsDisabledKwargs
  | RequestFormFieldsStaffKwargs
  | OfferFormFieldsKwargs
  | OfferFormFieldsDisabledKwargs
  | OfferFormFieldsStaffKwargs
  | AddressFormFieldsKwargs
  | RequestSharepointArticleFieldsKwargs
  | PurchasedPartFormFieldsKwargs
  | DimensionsFormFieldsKwargs
  | SharepointFormFieldsKwargs
  | EditSharepointFormFieldsKwargs
)

type RequestTableKwargs = {
  name: 'requestTableColumns'
  newRequests?: Request[]
  handleRequestingOffers?: (e: any, record: Request) => void
  handleOffer?: (e: any, record: any) => void
  handleDecline?: (e: any, record: any) => void
  decliningRequestId?: string | null
}

type OfferTableKwargs = {
  name: 'offerTableColumns'
  newOffers?: Offer[]
  handleContract?: (e: any, record: any) => void
  handleDecline?: (e: any, record: any) => void
}

type CertificateOptionsKwargs = {
  name: 'certificateOptions'
}

type RequestFormFieldsKwargs = {
  name: 'requestFormFields'
  certificateOptions: SelectOption[]
  selectedProduct: string
}

type RequestFormFieldsDisabledKwargs = {
  name: 'requestFormFieldsDisabled'
}

type RequestFormFieldsStaffKwargs = {
  name: 'requestFormFieldsStaff'
}

type OfferFormFieldsKwargs = {
  name: 'offerFormFields'
}

type OfferFormFieldsDisabledKwargs = {
  name: 'offerFormFieldsDisabled'
}

type OfferFormFieldsStaffKwargs = {
  name: 'offerFormFieldsStaff'
}

type AddressFormFieldsKwargs = {
  name: 'addressFormFields'
}

type RequestSharepointArticleFieldsKwargs = {
  name: 'requestSharepointArticleFields'
}

type PurchasedPartFormFieldsKwargs = {
  name: 'purchasedPartFormFields'
}

type DimensionsFormFieldsKwargs = {
  name: 'dimensionsFormFields'
  isRequiredDimension: (dimension: string) => boolean
}
type SharepointFormFieldsKwargs = {
  name: 'sharepointFormFields'
  feedbackFormOptions?: SelectOption[]
  onAddFeedbackFormOption?: () => void
  ArticleListComponent?: React.ReactElement
  ArticleDescriptionComponent?: React.ReactElement
  DurationInputComponent?: React.ReactElement
  DurationDescriptionComponent?: React.ReactElement
  t?: (key: string) => string
}
type EditSharepointFormFieldsKwargs = {
  name: 'editSharepointFormFields'
  SharedRequestComponent?: React.ReactElement
  RemainingDurationInputComponent?: React.ReactElement
  DurationDescriptionComponent?: React.ReactElement
  t?: (key: string) => string
}

export default function GetConstant(
  props: Props
): FormFieldType[] | ColumnProps<any>[] | SelectOption[] {
  const { t: tHook } = useTranslation(['common', 'request'])
  const { user } = useUserContext()
  // Use provided t function or fallback to hook for sharepoint form fields
  const t = (props.name === 'sharepointFormFields' || props.name === 'editSharepointFormFields')
    ? ((props as SharepointFormFieldsKwargs | EditSharepointFormFieldsKwargs).t || tHook)
    : tHook

  const renderStatus = (status: number) => {
    switch (status) {
      case 0:
        return (
          <Stack direction="row" spacing={1}>
            <Chip label={t('request:content.label.pending')} color="warning" variant="outlined" />
          </Stack>
        )
      case 1:
        return (
          <Stack direction="row" spacing={1}>
            <Chip label={t('request:content.label.accepted')} color="success" variant="outlined" />
          </Stack>
        )
      case 2:
        return (
          <Stack direction="row" spacing={1}>
            <Chip label={t('request:content.label.rejected')} color="error" variant="outlined" />
          </Stack>
        )
      case 3:
        return (
          <Stack direction="row" spacing={1}>
            <Chip label={t('request:content.label.forwarded')} color="primary" variant="outlined" />
          </Stack>
        )
      default:
        return (
          <Stack direction="row" spacing={1}>
            <Chip label={t('request:content.label.unknown')} color="default" variant="outlined" />
          </Stack>
        )
    }
  }

  if (props.name === 'requestTableColumns') {
    return [
      {
        title: t('request:content.label.requestNumber'),
        dataIndex: 'self',
        key: 'self',
        render: (self: any, record: Request) => {
          const requestNumber = getPKfromSelf(self)
          if (record.read) return requestNumber
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                color='primary'
                label={t('common:content.label.new')}
                size='small'
                sx={{ color: 'whitesmoke' }}
              />
              <span>{requestNumber}</span>
            </Stack>
          )
        },
        sorter: true,
        filter: {
          inputType: 'number',
          filterKey: 'request_number',
          groupBy: {
            key: 'requestInformation',
            label: t('request:content.label.requestInformation')
          },
        },
      },
      {
        title: t('common:content.label.organization', 'Organization'),
        dataIndex: 'organizationName',
        key: 'organization',
        render: (organizationName: string | null | undefined) => organizationName ?? '-',
        sorter: true,
        filter: {
          inputType: 'text',
          filterKey: 'organization',
          groupBy: {
            key: 'organizationDates',
            label: t('common:content.label.organizationDates')
          },
        },
      },
      {
        title: t('common:content.label.createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (date: any) => renderDateTime(date, { showTime: true }),
        sorter: true,
        filter: {
          inputType: 'date',
          filterKey: 'created_at',
          groupBy: {
            key: 'organizationDates',
            label: t('common:content.label.organizationDates')
          },
        },
      },
      {
        title: t('request:content.label.lastModifiedAt'),
        dataIndex: 'lastModifiedAt',
        key: 'lastModifiedAt',
        render: (date: any) => renderDateTime(date, { showTime: true }),
        sorter: true,
        filter: {
          inputType: 'date',
          filterKey: 'last_modified_at',
          groupBy: {
            key: 'organizationDates',
            label: t('common:content.label.organizationDates')
          },
        },
      },
      {
        title: t('request:content.label.remarks'),
        dataIndex: 'remarks',
        key: 'remarks',
        render: (remarks: any) => remarks ?? '-',
        filter: {
          inputType: 'text',
          filterKey: 'remarks',
          groupBy: {
            key: 'metadata',
            label: t('common:content.label.metadata')
          },
        },
      },
      {
        title: t('request:content.label.certificate'),
        dataIndex: 'certificate',
        key: 'certificate',
        render: (certificate: any) => certificate ?? '-',
        filter: {
          inputType: 'text',
          filterKey: 'certificate',
          groupBy: {
            key: 'metadata',
            label: t('common:content.label.metadata')
          },
        },
      },
      {
        title: t('request:content.label.description'),
        dataIndex: 'description',
        key: 'description',
        render: (description: any) => description ?? '-',
        filter: {
          inputType: 'text',
          filterKey: 'description',
          groupBy: {
            key: 'requestInformation',
            label: t('request:content.label.requestInformation')
          },
        },
      },
      {
        title: t('request:content.label.wishDate'),
        dataIndex: 'wishDate',
        key: 'wishDate',
        render: (date: any) => renderDateTime(date),
        sorter: true,
        filter: {
          inputType: 'date',
          filterKey: 'wish_date',
          groupBy: {
            key: 'requestInformation',
            label: t('request:content.label.requestInformation')
          },
        },
      },
      {
        title: t('request:content.label.status'),
        dataIndex: 'status',
        key: 'status',
        render: (status: number) => renderStatus(status),
        sorter: true,
        filter: {
          inputType: 'select',
          filterKey: 'status',
          groupBy: {
            key: 'requestInformation',
            label: t('request:content.label.requestInformation')
          },
        },
      },
      {
        title: t('request:content.label.priority'),
        dataIndex: 'priority',
        key: 'priority',
        render: (priority: boolean | undefined) =>
          priority !== undefined ? <>{priority ? <Check /> : <Close />}</> : '-',
        filter: {
          inputType: 'boolean',
          filterKey: 'priority',
          groupBy: {
            key: 'requestInformation',
            label: t('request:content.label.requestInformation')
          },
        },
      },
      {
        title: t('request:content.label.calculatedCosts'),
        dataIndex: 'calculatedCosts',
        key: 'calculatedCosts',
        render: (calculatedCosts: { [key: string]: any } | undefined) => {
          if (!calculatedCosts || Object.keys(calculatedCosts).length === 0) {
            return '-'
          }
          
          // Format price with German locale
          const formatPrice = (value: number) => value.toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
          
          return (
            <Box>
              {Object.entries(calculatedCosts).map(([quantityKey, costValue]) => {
                const quantity = parseFloat(quantityKey)
                const totalCost = parseNumericPrice(costValue as string | number | undefined)
                
                if (totalCost === null) {
                  return (
                    <Typography key={quantityKey} variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                      <strong>{quantityKey}</strong>: -
                    </Typography>
                  )
                }
                
                const unitCost = quantity > 0 ? totalCost / quantity : 0
                
                return (
                  <Typography key={quantityKey} variant="body2" sx={{ whiteSpace: 'nowrap' }}> 
                    <strong>{isNaN(quantity) ? quantityKey : quantity.toLocaleString('de-DE')}</strong>: {formatPrice(totalCost)} €
                  </Typography>
                )
              })}
            </Box>
          )
        },
        filter: {
          inputType: 'text',
          filterKey: 'calculated_costs',
          groupBy: {
            key: 'costInformation',
            label: t('request:content.label.costReport')
          },
        },
      },
      {
        title: t('request:content.label.calculatedCostBreakdown', 'Kostenaufschlüsselung'),
        dataIndex: 'calculatedCostBreakdown',
        key: 'calculatedCostBreakdown',
        render: (calculatedCostBreakdown: { [key: string]: any } | undefined) => {
          if (!calculatedCostBreakdown || Object.keys(calculatedCostBreakdown).length === 0) {
            return '-'
          }
          // Get the first quantity's breakdown for display
          const firstQuantity = Object.keys(calculatedCostBreakdown)[0]
          const breakdown = calculatedCostBreakdown[firstQuantity]
          if (!breakdown || typeof breakdown !== 'object') {
            return '-'
          }
          
          // Helper to map cost breakdown keys to labels
          const getCostLabel = (key: string): string => {
            const keyMap: { [key: string]: string } = {
              'estimated_price': t('request:content.label.estimatedPrice'),
              'estimated_material_cost': t('request:content.label.estimatedMaterialCost'),
              'estimated_manufacturing_cost': t('request:content.label.estimatedManufacturingCost'),
              'estimated_treatment_cost': t('request:content.label.estimatedTreatmentCost'),
              'estimated_investment_cost': t('request:content.label.estimatedInvestmentCost'),
              'remodelling_cost': t('request:content.label.remodellingCost'),
            }
            return keyMap[key] || key
          }
          
          // Format price value nicely
          const formatCostValue = (value: unknown): string => {
            const parsed = parseNumericPrice(value as string | number | undefined)
            if (parsed === null) return String(value)
            return parsed.toLocaleString('de-DE', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) + ' €'
          }
          
          const breakdownEntries = Object.entries(breakdown)
          const displayText = breakdownEntries.length > 0 
            ? `${breakdownEntries.length} ${t('common:content.label.entries', 'Einträge')}`
            : '-'
          
          return (
            <Tooltip
              title={
                <Box sx={{ p: 1 }}>
                  {Object.entries(calculatedCostBreakdown).map(([quantity, qBreakdown]) => (
                    <Box key={quantity} sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {t('request:content.label.quantity')}: {quantity}
                      </Typography>
                      {typeof qBreakdown === 'object' && qBreakdown !== null && 
                        Object.entries(qBreakdown).map(([key, value]) => (
                          <Typography key={key} variant="body2">
                            {getCostLabel(key)}: {formatCostValue(value)}
                          </Typography>
                        ))
                      }
                    </Box>
                  ))}
                </Box>
              }
              arrow
              placement="left"
            >
              <Chip 
                label={displayText} 
                size="small" 
                variant="outlined" 
                sx={{ cursor: 'pointer' }}
              />
            </Tooltip>
          )
        },
        filter: {
          inputType: 'text',
          filterKey: 'calculated_cost_breakdown',
          groupBy: {
            key: 'costInformation',
            label: t('request:content.label.costReport')
          },
        },
      },
      {
        title: t('request:content.label.reportedCosts'),
        dataIndex: 'reportedCosts',
        key: 'reportedCosts',
        render: (reportedCosts: { [key: string]: any } | undefined, record: Request) => {
          if (!reportedCosts || Object.keys(reportedCosts).length === 0) {
            return '-'
          }
          
          // Calculate lowest reported cost per quantity
          const quantities = record.quantities || []
          const lowestCosts: { [quantity: string]: number | undefined } = {}
          
          for (const quantity of quantities) {
            let lowest: number | undefined = undefined
            for (const supplier of Object.keys(reportedCosts)) {
              const supplierCosts = reportedCosts[supplier]
              if (supplierCosts && supplierCosts[quantity] !== undefined) {
                const cost = Number(supplierCosts[quantity])
                if (!isNaN(cost) && (lowest === undefined || cost < lowest)) {
                  lowest = cost
                }
              }
            }
            if (lowest !== undefined) {
              lowestCosts[String(quantity)] = lowest
            }
          }
          
          if (Object.keys(lowestCosts).length === 0) {
            return '-'
          }
          
          return (
            <Tooltip
              title={
                <Box sx={{ p: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {t('request:content.label.reportedCosts')}
                  </Typography>
                  {Object.entries(reportedCosts).map(([supplier, costs]) => (
                    <Box key={supplier} sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {supplier}:
                      </Typography>
                      {typeof costs === 'object' && costs !== null &&
                        Object.entries(costs).map(([qty, price]) => (
                          <Typography key={qty} variant="body2" sx={{ pl: 1 }}>
                            {qty}: {String(price)} €
                          </Typography>
                        ))
                      }
                    </Box>
                  ))}
                </Box>
              }
              arrow
              placement="left"
            >
              <Box>
                {Object.entries(lowestCosts).map(([quantity, cost]) => (
                  <Typography key={quantity} variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                    <strong>{quantity}</strong>: ab {cost} €
                  </Typography>
                ))}
              </Box>
            </Tooltip>
          )
        },
        filter: {
          inputType: 'text',
          filterKey: 'reported_costs',
          groupBy: {
            key: 'costInformation',
            label: t('request:content.label.costReport')
          },
        },
      },
      {
        title: t('request:content.label.customerPriceSuggestion'),
        dataIndex: 'customerPriceSuggestion',
        key: 'customerPriceSuggestion',
        render: (customerPriceSuggestion: string | undefined) => {
          if (!customerPriceSuggestion) {
            return '-'
          }
          // Format number with thousand separators (German locale)
          const numValue = parseFloat(customerPriceSuggestion)
          if (isNaN(numValue)) {
            return `${customerPriceSuggestion} €`
          }
          const formattedValue = numValue.toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
          return `${formattedValue} €`
        },
        filter: {
          inputType: 'text',
          filterKey: 'customer_price_suggestion',
          groupBy: {
            key: 'costInformation',
            label: t('request:content.label.costReport')
          },
        },
      },
      {
        title: t('common:content.label.editors'),
        key: 'editors',
        dataIndex: 'editors',
        render: (text: string, record: Request) => {
          if (Array.isArray(record.editors)) {
            return (
              <Stack direction="row" spacing={1}>
                {record.editors.map((e: User) => (
                  <Chip key={e.id} label={getOrgUserRepresentation(e as OrganizationUser)} />
                ))}
              </Stack>
            )
          } else {
            return ''
          }
        },
        filter: {
          inputType: 'text',
          filterKey: 'editors',
          groupBy: {
            key: 'metadata',
            label: t('common:content.label.metadata')
          },
        },
      },
      {
        title: t('common:content.label.action'),
        key: 'action',
        dataIndex: 'action',
        render: (text: string, record: any) => (
          <Stack direction="column" spacing={1}>
            <Button
              id="requestQuotesButton"
              size="small"
              variant="contained"
              color="inherit"
              onClick={e => props.handleRequestingOffers && props.handleRequestingOffers(e, record)}
              disabled={record.status >= 1}
              style={{ marginBottom: '0.75rem' }}
            >
              {t('request:interaction.button.requestQuotes')}
            </Button>
            <Button
              id="offerByAssembleanButton"
              size="small"
              variant="contained"
              onClick={e => props.handleOffer && props.handleOffer(e, record)}
              disabled={record.status === 2}
            >
              {t('request:interaction.button.createOffer')}
            </Button>
            <LoadingButton
              id="declineRequestButton"
              size="small"
              variant="outlined"
              color="error"
              onClick={e => props.handleDecline && props.handleDecline(e, record)}
              disabled={record.status === 2 || record.status === 1}
              loading={props.decliningRequestId === record.self}
            >
              {t('request:interaction.button.declineRequest')}
            </LoadingButton>
          </Stack>
        ),
      },
    ].filter(
      column =>
        (!isPrivileged(user, 'STAFF') &&
          column.key !== 'editors' &&
          column.key !== 'priority' &&
          column.key !== 'organization' &&
          column.key !== 'action' &&
          column.key !== 'calculatedCostBreakdown' &&
          column.key !== 'reportedCosts') ||
        isPrivileged(user, 'STAFF')
    ) as SmartTableColumnType<Request>[]
  }

  if (props.name === 'offerTableColumns') {
    return [
      {
        title: t('request:content.label.offerNumber'),
        dataIndex: 'self',
        key: 'self',
        render: (self: any, record: Offer) => {
          const offerNumber = getPKfromSelf(self)
          if (record.read) return offerNumber
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                color='primary'
                label={t('common:content.label.new')}
                size='small'
                sx={{ color: 'whitesmoke' }}
              />
              <span>{offerNumber}</span>
            </Stack>
          )
        },
        sorter: true,
        filter: {
          inputType: 'number',
          filterKey: 'offer_number',
          groupBy: {
            key: 'offerInformation',
            label: t('request:content.label.offerInformation')
          },
        },
      },
      {
        title: t('request:content.label.requestNumber'),
        dataIndex: 'request',
        key: 'request',
        render: (request: any) => (request?.self ? getPKfromSelf(request.self) : '-'),
        sorter: true,
        filter: {
          inputType: 'number',
          filterKey: 'request_number',
          groupBy: {
            key: 'offerInformation',
            label: t('request:content.label.offerInformation')
          },
        },
      },
      {
        title: t('request:content.label.article'),
        dataIndex: 'article',
        key: 'article',
        render: (article: any) => article?.name ?? '-',
        sorter: true,
        filter: {
          inputType: 'text',
          filterKey: 'article',
          groupBy: {
            key: 'offerInformation',
            label: t('request:content.label.offerInformation')
          },
        },
      },
      {
        title: t('common:content.label.createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (date: any) => renderDateTime(date, { showTime: true }),
        sorter: true,
        filter: {
          inputType: 'date',
          filterKey: 'created_at',
          groupBy: {
            key: 'dates',
            label: t('common:content.label.dates')
          },
        },
      },
      {
        title: t('common:content.label.lastModifiedAt'),
        dataIndex: 'lastModifiedAt',
        key: 'lastModifiedAt',
        render: (date: any) => renderDateTime(date, { showTime: true }),
        sorter: true,
        filter: {
          inputType: 'date',
          filterKey: 'last_modified_at',
          groupBy: {
            key: 'dates',
            label: t('common:content.label.dates')
          },
        },
      },
      {
        title: t('request:content.label.remarks'),
        dataIndex: 'remarks',
        key: 'remarks',
        render: (remarks: any) => remarks ?? '-',
        filter: {
          inputType: 'text',
          filterKey: 'remarks',
          groupBy: {
            key: 'metadata',
            label: t('common:content.label.metadata')
          },
        },
      },
      {
        title: t('request:content.label.prices'),
        dataIndex: 'prices',
        key: 'prices',
        render: (prices: PriceObject) => {
          if (!prices || Object.keys(prices).length === 0) {
            return '-'
          }
          return (
            <>
              {Object.keys(prices).map((k: string) => (
                <div key={k}>
                  <strong>{k}</strong>: {prices[k] + ' €'}
                </div>
              ))}
            </>
          )
        },
        filter: {
          inputType: 'text',
          filterKey: 'prices',
          groupBy: {
            key: 'offerInformation',
            label: t('request:content.label.offerInformation')
          },
        },
      },
      {
        title: t('request:content.label.status'),
        dataIndex: 'status',
        key: 'status',
        render: (status: number) => renderStatus(status),
        sorter: true,
        filter: {
          inputType: 'select',
          filterKey: 'status',
          groupBy: {
            key: 'offerInformation',
            label: t('request:content.label.offerInformation')
          },
        },
      },
      {
        title: t('common:content.label.editors'),
        key: 'editors',
        dataIndex: 'editors',
        render: (text: string, record: Offer) => {
          if (Array.isArray(record.editors)) {
            return (
              <>
                {record.editors.map((e: User) => (
                  <Chip
                    key={e.id}
                    label={`${e.firstName} ${e.lastName}`}
                    style={{ marginRight: '5px' }}
                  />
                ))}
              </>
            )
          } else {
            return ''
          }
        },
        filter: {
          inputType: 'text',
          filterKey: 'editors',
          groupBy: {
            key: 'metadata',
            label: t('common:content.label.metadata')
          },
        },
      },
      {
        title: t('common:content.label.action'),
        key: 'action',
        dataIndex: 'action',
        render: (text: string, record: any) => (
          <Stack direction="column" spacing={1}>
            <Button
              id="acceptOfferButton"
              size="small"
              variant="contained"
              onClick={e => props.handleContract && props.handleContract(e, record)}
              disabled={record.status === 2}
            >
              {t('request:interaction.button.createContract')}
            </Button>
            <Button
              id="declineOfferButton"
              size="small"
              variant="outlined"
              color="error"
              onClick={e => props.handleDecline && props.handleDecline(e, record)}
              disabled={record.status === 2 || record.status === 1}
            >
              {t('request:interaction.button.declineOffer')}
            </Button>
          </Stack>
        ),
      },
    ].filter(
      column =>
        (!isPrivileged(user, 'STAFF') && column.key !== 'editors') ||
        (isPrivileged(user, 'STAFF') && column.key !== 'action')
    ) as SmartTableColumnType<Offer>[]
  }

  if (props.name === 'certificateOptions') {
    return [
      { value: 'AS 9100D', label: 'AS 9100D' },
      { value: 'DIN EN ISO 13485', label: 'DIN EN ISO 13485' },
      { value: 'DIN EN ISO 9001', label: 'DIN EN ISO 9001' },
      { value: 'NADCAP', label: 'NADCAP' },
      { value: 'ISO 14001', label: 'ISO 14001' },
      { value: 'IATF 16949', label: 'IATF 16949' },
      { value: 'ISO 27001', label: 'ISO 27001' },
      { value: 'BS7799', label: 'BS7799' },
      { value: 'ISO 50001', label: 'ISO 50001' },
      { value: 'EN ISO 17065', label: 'EN ISO 17065' },
      { value: 'SA8000', label: 'SA8000' },
      { value: 'EMAS', label: 'EMAS' },
      { value: 'DIN EN 1090', label: 'DIN EN 1090' },
      { value: 'DIN EN ISO 3834', label: 'DIN EN ISO 3834' },
      { value: 'DIN 2304', label: 'DIN 2304' },
      { value: 'DIN EN ISO 13585', label: 'DIN EN ISO 13585' },
      { value: 'DIN ISO 11745', label: 'DIN ISO 11745' },
    ] as SelectOption[]
  }

  if (props.name === 'requestFormFieldsDisabled') {
    return [
      {
        type: 'text',
        key: 'name',
        label: t('contract:content.label.selectedProduct'),
        xs: 12,
        sm: 6,
      },
      {
        type: 'text',
        key: 'number',
        label: t('contract:content.label.articleNumber'),
        xs: 12,
        sm: 6,
      },
    ]
  }

  if (props.name === 'requestFormFields') {
    return [
      {
        type: 'multiline',
        key: 'description',
        label: t('request:content.label.description'),
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
      {
        type: 'select-search',
        key: 'certificate',
        label: t('common:content.label.certificate'),
        options: props.certificateOptions,
        validation: false,
        required: false,
        xs: 12,
        sm: 6,
      },
      {
        type: 'date',
        key: 'wishDate',
        label: t('request:content.label.wishDate'),
        required: false,
        validation: false,
        xs: 12,
        sm: 6,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'requestFormFieldsStaff') {
    return [
      {
        type: 'multiline',
        key: 'remarks',
        label: t('request:content.label.remarks'),
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
      {
        type: 'select-search-multi',
        key: 'editors',
        label: t('common:content.label.editors'),
        autocompleteField: 'staff-users',
        required: false,
        validation: false,
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
      {
        type: 'checkbox',
        key: 'priority',
        label: t('request:content.label.priorityText'),
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
    ]
  }

  if (props.name === 'offerFormFields') {
    return [
      {
        type: 'multiline',
        key: 'remarks',
        label: t('request:content.label.remarks'),
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'offerFormFieldsDisabled') {
    return [
      {
        type: 'text',
        key: 'name',
        label: t('contract:content.label.selectedProduct'),
        xs: 12,
        sm: 6,
      },
      {
        type: 'text',
        key: 'number',
        label: t('contract:content.label.articleNumber'),
        xs: 12,
        sm: 6,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'offerFormFieldsStaff') {
    return [
      {
        type: 'select-search-multi',
        key: 'editors',
        label: t('common:content.label.editors'),
        autocompleteField: 'staff-users',
        required: false,
        validation: false,
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
      {
        type: 'multiline',
        key: 'remarks',
        label: t('request:content.label.remarks'),
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'addressFormFields') {
    // Destination country is no longer required - return empty array
    return []
  }

  if (props.name === 'requestSharepointArticleFields') {
    return [
      {
        type: 'text',
        key: 'name',
        label: t('common:content.label.articleName'),
        required: false,
      },
      {
        type: 'text',
        key: 'number',
        label: t('common:content.label.articleNumber'),
        required: true,
        validation: true,
      },
      {
        type: 'multiline',
        key: 'description',
        label: t('common:content.label.description'),
        xl: 12,
      },
      {
        type: 'text',
        key: 'barcodeText',
        label: t('common:content.label.barcode'),
      },
      {
        type: 'text',
        key: 'barcodeType',
        label: t('common:content.label.barcodeType'),
        autocompleteField: 'barcode-types',
        /* options: [
            { value: 'ean', label: 'EAN' },
            { value: 'gtin', label: 'GTIN' },
            { value: 'upc', label: 'UPC' },
            { value: 'custom', label: t('common:content.label.custom') as string},
          ], */
      },
      {
        type: 'text',
        key: 'type',
        label: t('common:content.label.type'),
        autocompleteField: 'article-types',
      },
      {
        type: 'text',
        key: 'family',
        label: t('common:content.label.family'),
        autocompleteField: 'families',
      },
      {
        type: 'select-search-multi',
        key: 'properties',
        label: t('common:content.label.properties'),
        autocompleteField: 'properties',
        required: false,
        validation: false,
      },
      {
        type: 'select-search-multi',
        key: 'category',
        label: t('common:content.label.categories'),
        autocompleteField: 'categories',
        required: false,
        validation: false,
      },
      {
        type: 'text',
        key: 'brand',
        label: t('common:content.label.brand'),
        autocompleteField: 'brands',
      },
      {
        type: 'select-search',
        key: 'variationOf',
        label: t('common:content.label.variationOf'),
        //options: props.articleOptions,
        disabled: true,
      },
      {
        type: 'select-search',
        key: 'qualityClass',
        label: t('common:content.label.qualityClass'),
        options: [
          { value: 'f', label: 'f' },
          { value: 'm', label: 'm' },
          { value: 'c', label: 'c' },
          { value: 'v', label: 'v' },
        ],
        required: false,
        validation: false,
      },
    ]
  }

  if (props.name === 'purchasedPartFormFields') {
    return [
      {
        type: 'select-search',
        key: 'supplier',
        label: t('common:content.label.supplier'),
        //options: props.supplierOptions,
        // loadData: loadSupplierData,
        // onAddOption: (canCreate(userPermissions, PERMISSIONS.SUPPLIER.SUPPLIER) || isPrivileged(user)) && props.onAddSupplierOption,
        autocompleteField: 'suppliers',
        required: true,
        validation: true,
      },
      {
        type: 'select-search',
        key: 'countryOfOrigin',
        label: t('common:content.label.countryOfOrigin'),
        autocompleteField: 'countries',
        required: false,
        validation: false,
      },
      {
        type: 'text',
        key: 'minimumOrder',
        label: t('common:content.label.minimumOrder'),
      },

      {
        type: 'text',
        key: 'batchSize',
        label: t('common:content.label.batchSize'),
      },
      {
        type: 'timedelta',
        key: 'leadTime',
        label: t('common:content.label.leadTime'),
        xl: 6,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'dimensionsFormFields') {
    // Metric unit options
    const weightUnitOptions = [
      { value: 'mg', label: 'mg' },
      { value: 'g', label: 'g' },
      { value: 'kg', label: 'kg' },
    ]
    const lengthUnitOptions = [
      { value: 'mm', label: 'mm' },
      { value: 'cm', label: 'cm' },
      { value: 'm', label: 'm' },
      { value: 'km', label: 'km' },
    ]
    const volumeUnitOptions = [
      { value: 'cubic_millimeter', label: 'mm³' },
      { value: 'cubic_centimeter', label: 'cm³' },
      { value: 'cubic_meter', label: 'm³' },
    ]

    return [
      {
        type: 'measurement',
        key: 'weight',
        label: t('common:content.label.weight'),
        errorMessage: t('item:feedback.error.measurementInput') ,
        required: props.isRequiredDimension('weight'),
        unit: 'g',
        unitOptions: weightUnitOptions,
      },
      {
        type: 'measurement',
        key: 'length',
        label: t('common:content.label.length'),
        errorMessage: t('item:feedback.error.measurementInput') ,
        required: props.isRequiredDimension('length'),
        unit: 'm',
        unitOptions: lengthUnitOptions,
      },
      {
        type: 'measurement',
        key: 'width',
        label: t('common:content.label.width'),
        errorMessage: t('item:feedback.error.measurementInput') ,
        required: props.isRequiredDimension('width'),
        unit: 'm',
        unitOptions: lengthUnitOptions,
      },
      {
        type: 'measurement',
        key: 'height',
        label: t('common:content.label.height'),
        errorMessage: t('item:feedback.error.measurementInput') ,
        required: props.isRequiredDimension('height'),
        unit: 'm',
        unitOptions: lengthUnitOptions,
      },
      {
        type: 'measurement',
        key: 'volume',
        label: t('common:content.label.volume'),
        errorMessage: t('item:feedback.error.measurementInput') ,
        required: props.isRequiredDimension('volume'),
        unit: 'm³',
        unitOptions: volumeUnitOptions,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'sharepointFormFields') {
    const sharepointProps = props as SharepointFormFieldsKwargs
    return [
      {
        type: 'custom',
        key: 'articleList',
        label: t('common:content.label.article'),
        CustomComponent: sharepointProps.ArticleListComponent,
        xs: 6,
        sm: 6,
        md: 6,
        lg: 6,
        xl: 6,
      },
      {
        type: 'custom',
        key: 'articleDescription',
        label: '',
        CustomComponent: sharepointProps.ArticleDescriptionComponent,
        xs: 6,
        sm: 6,
        md: 6,
        lg: 6,
        xl: 6,
      },
      {
        type: 'custom',
        key: 'sharepointDuration',
        label: t('sharepoint:content.label.sharepointDuration'),
        CustomComponent: sharepointProps.DurationInputComponent,
        xs: 6,
        sm: 6,
        md: 6,
        lg: 6,
        xl: 6,
      },
      {
        type: 'custom',
        key: 'durationDescription',
        label: '',
        CustomComponent: sharepointProps.DurationDescriptionComponent,
        xs: 6,
        sm: 6,
        md: 6,
        lg: 6,
        xl: 6,
      },
      {
        type: 'select-search',
        key: 'feedbackForm',
        label: t('sharepoint:content.label.feedbackForm'),
        options: sharepointProps.feedbackFormOptions,
        onAddOption: sharepointProps.onAddFeedbackFormOption,
        required: false,
        validation: false,
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
        xl: 12,
      },
    ] as FormFieldType[]
  }

  if (props.name === 'editSharepointFormFields') {
    const editSharepointProps = props as EditSharepointFormFieldsKwargs
    return [
      {
        type: 'custom',
        key: 'sharedRequest',
        CustomComponent: editSharepointProps.SharedRequestComponent,
        xs: 8,
        sm: 8,
        md: 8,
        lg: 8,
        xl: 8,
      },
      {
        type: 'custom',
        key: 'remainingSharepointDuration',
        CustomComponent: editSharepointProps.RemainingDurationInputComponent,
        xs: 8,
        sm: 8,
        md: 8,
        lg: 8,
        xl: 8,
      },
      {
        type: 'custom',
        key: 'durationDescription',
        CustomComponent: editSharepointProps.DurationDescriptionComponent,
        xs: 4,
        sm: 4,
        md: 4,
        lg: 4,
        xl: 4,
      },
    ] as FormFieldType[]
  }

  return [] as FormFieldType[] | ColumnProps<any>[] | SelectOption[]
}
